import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../navigation/AuthProvider";

export const PayInfoPopup = ({ onClose }) => {
  const { vacationPay } = useAuth();

  // Semesterersättning:
  // - Aktiv inställning → använd företagets procentsats
  // - Inaktiv inställning → 0%
  // - Ingen inställning alls → 12% (fallback)
  const vacationPercentage =
    vacationPay && vacationPay.vacationPayActive
      ? vacationPay.vacationPayPercentage
      : vacationPay
      ? 0
      : 12;

  const isVacationActive = vacationPay?.vacationPayActive;

  // Scroll-indikator (visas bara om det finns mer innehåll att skrolla)
  const scrollRef = useRef(null);
  const [canScroll, setCanScroll] = useState(false);
  const [atBottom, setAtBottom] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      setCanScroll(el.scrollHeight > el.clientHeight + 1);
      setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 2);
    };

    update();
    const onScroll = () => update();
    el.addEventListener("scroll", onScroll);

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        top: 0, left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000
      }}
      onClick={onClose}
    >
      {/* Liten keyframe för den rörliga pilen */}
      <style>
        {`
          @keyframes bounceDown {
            0%, 100% { transform: translate(-50%, 0); opacity: .85; }
            50% { transform: translate(-50%, 6px); opacity: 1; }
          }
        `}
      </style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "32px",
          maxWidth: "580px",
          width: "90%",
          boxShadow: "0 6px 24px rgba(0, 0, 0, 0.2)",
          textAlign: "left",
          position: "relative",
          fontFamily: "'Inter', sans-serif",
          // Max 80% av skärmen – innehållet inuti kan skrollas
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column"
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "none",
            border: "none",
            fontSize: "22px",
            fontWeight: "bold",
            cursor: "pointer",
            color: "#999"
          }}
          aria-label="Stäng"
          title="Stäng"
        >
          &times;
        </button>

        <h2 style={{ marginTop: 0, fontSize: "22px", color: "#1c1c1c", marginBottom: "18px" }}>
          Så här beräknas lönen
        </h2>

        {/* Scrollbart innehåll */}
        <div
          ref={scrollRef}
          style={{
            overflowY: "auto",
            paddingRight: 6
          }}
        >
          {/* 1. Arbetade timmar */}
          <p style={{ marginBottom: "10px", color: "#333", lineHeight: 1.6 }}>
            <strong>1) Arbetade timmar</strong>
          </p>
          <ul style={{ margin: "0 0 14px 20px", color: "#333", lineHeight: 1.6 }}>
            <li><strong>Ordinarie tid</strong> minus <strong>rast</strong> minus <strong>frånvaro</strong> (enligt reglerna ni satt).</li>
            <li><strong>Övertid</strong> läggs ovanpå enligt företagets övertidsregler.</li>
          </ul>

          {/* 2. Bruttolön */}
          <p style={{ marginBottom: "10px", color: "#333", lineHeight: 1.6 }}>
            <strong>2) Bruttolön</strong>
          </p>
          <ul style={{ margin: "0 0 14px 20px", color: "#333", lineHeight: 1.6 }}>
            <li><strong>Grundlön</strong> = Arbetade timmar × Timlön</li>
            <li><strong>+ OB-tillägg</strong> (om tillämpligt)</li>
            <li><strong>+ Övertidsersättning</strong> (enligt övertidsregler)</li>
            <li><strong>± Frånvaroersättning/avdrag</strong> (enligt era frånvaroregler)</li>
          </ul>

          {/* 3. Frånvaro – hur det räknas */}
          <p style={{ marginBottom: "10px", color: "#333", lineHeight: 1.6 }}>
            <strong>Frånvaro – enligt era regler</strong>
          </p>
          <p style={{ margin: "0 0 14px 0", color: "#333", lineHeight: 1.6 }}>
            Varje <strong>frånvarotyp</strong> (t.ex. sjuk, VAB) styrs av den <strong>procentsats/ersättningsnivå</strong> ni har satt
            i <em>Frånvaroregler</em>. Systemet räknar bara den del av frånvaron som <strong>ligger inom passets tider</strong>.
            Denna del ersätts eller dras av enligt regeln (t.ex. 80%, 100% eller 0%).
          </p>

          {/* 4. Semesterersättning */}
          <p style={{ marginBottom: "10px", color: "#333", lineHeight: 1.6 }}>
            <strong>3) Semesterersättning</strong>
          </p>
          <p style={{ margin: "0 0 14px 0", color: "#333", lineHeight: 1.6 }}>
            Läggs <em>efteråt</em> på bruttolönen: <strong>{vacationPercentage}%</strong>.
            {isVacationActive === false && " (Just nu inaktiv för företaget.)"}
          </p>

          {/* 5. Skatt-info */}
          <p style={{ marginBottom: "14px", color: "#666", fontSize: "14px", lineHeight: 1.5 }}>
            Denna vy visar <strong>bruttolön före skatt</strong>. Nettolönen påverkas av skattesats och eventuella andra avdrag.
          </p>

          {/* Sammanfattad formel */}
          <div style={{
            backgroundColor: "#f4f4f4",
            padding: "16px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            fontFamily: "monospace",
            fontSize: "14px",
            color: "#333"
          }}>
            Arbetade timmar = Ordinarie − Rast − Frånvaro<br />
            Grundlön = Arbetade timmar × Timlön<br />
            Bruttolön = Grundlön + OB + Övertid ± Frånvaro (enl. regler)<br />
            Semesterersättning = Bruttolön × {vacationPercentage}%<br />
            <strong>Total lön före skatt</strong> = Bruttolön + Semesterersättning
          </div>

          <div style={{ height: 8 }} />
        </div>

        {/* Scroll-hint: visas bara när det finns mer att skrolla och man inte är längst ner */}
        {canScroll && !atBottom && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: "50%",
              bottom: 10,
              transform: "translateX(-50%)",
              fontSize: 12,
              color: "#666",
              userSelect: "none",
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >
            <span>Skrolla</span>
            <span
              style={{
                width: 10,
                height: 10,
                borderRight: "2px solid #666",
                borderBottom: "2px solid #666",
                transform: "rotate(45deg)",
                animation: "bounceDown 1.2s infinite ease-in-out"
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PayInfoPopup;
